/**
 * ============================================================================
 * CALLOUT PANEL — the unified WBR callouts surface (Figma: WBR-callout-UX).
 * ============================================================================
 *
 * One right-edge panel that replaces the old split (CalloutRail + CalloutDrawer).
 * A collapsed tab advertises the feature; expanded it shows a week selector +
 * download and two tabs:
 *
 *   Metric — the SELECTED cell's detail: a current-vs-prior value card, the
 *            one WBR callout (write / edit / delete), the comment + reply
 *            thread, and a "‹ n/m callouts ›" pager that steps through every
 *            cell in the week that carries a callout/comment.
 *   All    — the week roll-up list (every cell with a callout/comment) with
 *            Week / Category / Metric / Search filters. Clicking an item
 *            selects that cell and flips to the Metric tab.
 *
 * Selection is PAGE-LEVEL (dashboardSlice.selectedCalloutCell) so a grid cell
 * click in any table section drives this single shared panel.
 *
 * AI callouts (WBR Agent raw analysis + AI summary + Regenerate) from the Figma
 * are intentionally OUT OF SCOPE: the HILT model has no GenerateMetricCallout
 * operation. They slot into the Metric tab when the backend adds it — no fake
 * text is rendered in the meantime.
 *
 * Reads/writes go through metricCommentsApi; a write invalidates the cell + week
 * tags so both the thread and the list/pager refresh with no manual reload.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Header from '@amzn/awsui-components-core/header';
import Spinner from '@amzn/awsui-components-core/spinner';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import FormField from '@amzn/awsui-components-core/form-field';
import Select from '@amzn/awsui-components-core/select';
import Input from '@amzn/awsui-components-core/input';
import { ThreadActionItems } from 'src/components/actionitems/ThreadActionItems';
import Icon from '@amzn/awsui-components-core/icon';
import Textarea from '@amzn/awsui-components-core/textarea';
import Link from '@amzn/awsui-components-core/link';
import Modal from '@amzn/awsui-components-core/modal';
import { useAppSelector, useAppDispatch } from 'src/app/store';
import {
  calloutCellSelected,
  calloutCellCleared,
  dataModeChanged,
} from 'src/features/dashboard/dashboardSlice';
import {
  useGetWeekCalloutsQuery,
  useGetCellCommentsQuery,
  useWriteCalloutMutation,
  useDeleteCalloutMutation,
  useCreateCommentMutation,
  useEditCommentMutation,
  useDeleteCommentMutation,
  useCreateReplyMutation,
  useEditReplyMutation,
  useDeleteReplyMutation,
} from 'src/features/api/metricCommentsApi';
import { useGetWeekNumbersQuery } from 'src/features/api/weekNumbersApi';
import { useMetricCatalog } from 'src/features/api/useMetricCatalog';
import {
  BTN_ADD_COMMENT,
  BTN_ADD_REPLY,
  BTN_CANCEL,
  BTN_POST_CALLOUT,
  BTN_SAVE,
  PLACEHOLDER_CALLOUT,
  PLACEHOLDER_COMMENT,
  PLACEHOLDER_REPLY,
  ERROR_POST_CALLOUT,
  ERROR_DELETE_CALLOUT,
  ERROR_ADD_COMMENT,
  ERROR_EDIT_COMMENT,
  ERROR_DELETE_COMMENT,
  ERROR_ADD_REPLY,
  ERROR_EDIT_REPLY,
  ERROR_DELETE_REPLY,
} from 'src/constants/metricCallouts';
import { CalloutAuthor } from 'src/components/table/CalloutAuthor';
import {
  toCanonicalWeekToken,
  toWeekLabel,
} from 'src/features/api/durationRange';
import { relativeTime } from 'src/components/table/relativeTime';
import { dashboardStructure, dashboardPath } from 'src/config/dashboards';
import { buildCatalogView } from 'src/features/api/catalogView';
import {
  currentUserAlias,
  isOwnRecord,
  sameActor,
} from 'src/accessors/currentUser';
import {
  CalloutSelection,
  CellCalloutSummary,
  Comment,
  selectionAnchor,
} from 'src/types/metricComments.types';

interface Option {
  label: string;
  value: string;
}
// NOTE: HILT keys callouts by (metricKey, reportingPeriod) with NO network
// dimension — unlike CTS, where "category" is the network/section title. So
// this filters by the metric's CATALOG GROUP (metricGroup), which is the only
// grouping HILT callouts carry. Per-network callouts would need a backend
// model change (add a network/category to the comment key).
const ALL_GROUPS: Option = { label: 'All groups', value: '' };
const ALL_METRICS: Option = { label: 'All metrics', value: '' };

/**
 * Escape a CSV field. Two concerns: (1) quote when it contains a
 * comma/quote/newline; (2) neutralize FORMULA INJECTION — a field starting with
 * `=`, `+`, `-`, `@` (or a leading tab/CR) is executed as a formula by
 * Excel/Sheets, and callout/metric text is user-authored. Prefix such a field
 * with a single quote to force plain text.
 */
function csvField(v: string): string {
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export const CalloutPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'metric' | 'all'>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // All-tab filters + panel week selection.
  const [weekToken, setWeekToken] = useState('');
  const [category, setCategory] = useState('');
  const [metricFilter, setMetricFilter] = useState('');
  const [search, setSearch] = useState('');

  // Metric-tab editors (one open at a time).
  const [editingCallout, setEditingCallout] = useState(false);
  const [calloutDraft, setCalloutDraft] = useState('');
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [replyEditDraft, setReplyEditDraft] = useState('');
  /** Surfaces a friendly message when a callout/comment/reply write fails (CTS parity). */
  const [writeError, setWriteError] = useState<string | null>(null);
  /** Pending delete for the confirmation modal (replaces the native window.confirm). */
  const [pendingDelete, setPendingDelete] = useState<{
    kind: 'callout' | 'comment' | 'reply';
    id: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const reportingWeek = useAppSelector((s) => s.dashboard.reportingWeek);
  const selectedCell = useAppSelector((s) => s.dashboard.selectedCalloutCell);
  // The served dashboard being viewed (multi-dashboard R3.1) — joins the
  // catalog/week queries and every anchor this panel creates.
  const dashboardId = useAppSelector((s) => s.dashboard.activeDashboardId);
  // Callouts are snapshot data in every mode (the panel also mounts on the
  // Action Items page in live). One mode keeps one cache entry per cell, so a
  // write from either page refreshes the thread the other page reads.
  const apiDataMode = 'SNAPSHOT';

  // ONE authoring surface (spec metric-callouts R1.7): the page the active
  // dashboard's structure flags calloutAuthoring (the Detailed View). Matched
  // against the structure's routes so a future dashboard can pick its own.
  // Writability is further gated by the active tab type below.
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const structure = dashboardStructure(dashboardId);
  const authoringPage = structure.pages.find((p) => p.calloutAuthoring);
  // The dashboard rides the URL path ("/kfe-wbr/detailed-view"), so compare
  // against the slug-prefixed authoring path, not the bare structure route.
  const authoringPath = authoringPage
    ? dashboardPath(dashboardId, authoringPage.route)
    : undefined;
  const onAuthoringPath =
    authoringPath !== undefined &&
    (pathname === authoringPath || pathname.startsWith(`${authoringPath}/`));

  const { data: weekData } = useGetWeekNumbersQuery();
  const { catalog, nameOf, groupOf, definitionsResponse } =
    useMetricCatalog(dashboardId);

  // Active tab TYPE on the authoring page (spec metric-callouts R1.7):
  //   table-sections → writable   deep-dive → read-only   glossary → hidden
  // `?tab=` selects the tab (default: first); undefined off the authoring
  // page (Home / Customer Domain — read-only via path).
  const activeTabType = useMemo(() => {
    if (!onAuthoringPath || !authoringPage) return undefined;
    // The page's own view: hand-authored WbrView, or catalog-derived for a
    // `view: 'catalog'` dashboard (Quality).
    const view =
      authoringPage.view && authoringPage.view !== 'catalog'
        ? authoringPage.view
        : buildCatalogView(definitionsResponse?.definitions);
    const tabId = searchParams.get('tab');
    const active = view.tabs.find((t) => t.id === tabId) ?? view.tabs[0];
    return active?.type;
  }, [onAuthoringPath, authoringPage, definitionsResponse, searchParams]);

  // Writes only on a table-sections tab of the authoring page. Deep dive is
  // read-only; everywhere else (Home, Domain, glossary) is not writable.
  const canWrite = onAuthoringPath && activeTabType === 'table-sections';
  // Callouts are snapshot-only, so the authoring page is only useful in
  // snapshot mode. Switch before navigating: from the Action Items page in
  // live mode, navigating alone would unmount this panel (App mounts it for
  // snapshot OR that page) and drop the thread the user was just reading.
  const goToAuthoringPage = () => {
    if (!authoringPath) return;
    dispatch(dataModeChanged('snapshot'));
    navigate(authoringPath);
  };
  // The glossary tab has no metric cells — the panel and its rail do not
  // belong there at all.
  const hiddenForTab = activeTabType === 'glossary';

  // A grid cell click (page-level selection) opens the panel on its Metric tab
  // and syncs the week selector to the clicked cell's week.
  useEffect(() => {
    if (selectedCell) {
      setOpen(true);
      setTab('metric');
      setWeekToken(selectedCell.reportingPeriod);
      // Reset ALL per-cell editor state on a cell change. The submit handlers
      // derive `anchor` from the current selectedCell, so any in-progress draft
      // left from cell A would otherwise be submitted against cell B when the
      // user switches (via the pager or an All-tab row). writeError belongs to
      // the old cell too.
      setWriteError(null);
      setEditingCallout(false);
      setCalloutDraft('');
      setNewComment('');
      setEditingCommentId(null);
      setCommentDraft('');
      setReplyingToId(null);
      setReplyDraft('');
      setEditingReplyId(null);
      setReplyEditDraft('');
    }
  }, [selectedCell]);

  // A selection only means something while this panel exists. Clear it on
  // unmount so a cell picked in snapshot doesn't auto-open the panel the next
  // time it mounts (arriving at Action Items in live mode) or leave a
  // highlighted cell in a mode that has no panel.
  useEffect(
    () => () => {
      dispatch(calloutCellCleared());
    },
    [dispatch],
  );

  // Reserve layout space for the docked panel: while it's open, the page
  // content gets right padding equal to the panel width (see index.scss
  // `.wbr-callouts-open`) so the panel sits BESIDE the tables, never over them.
  // Cleared on close/unmount.
  useEffect(() => {
    const cls = 'wbr-callouts-open';
    // Never reserve the panel gutter where the panel is hidden (glossary).
    document.body.classList.toggle(cls, open && !hiddenForTab);
    return () => document.body.classList.remove(cls);
  }, [open, hiddenForTab]);
  // The COLLAPSED rail tab also reserves its slim gutter — content must never
  // sit underneath it (it overlapped the rightmost cards/columns otherwise).
  useEffect(() => {
    const cls = 'wbr-callouts-rail';
    document.body.classList.toggle(cls, !open && !hiddenForTab);
    return () => document.body.classList.remove(cls);
  }, [open, hiddenForTab]);

  // Active week: the selected cell's week (Metric tab), else the panel's own
  // selection, else the dashboard reporting week, else the current week.
  const period = useMemo(() => {
    const week =
      (tab === 'metric' && selectedCell?.reportingPeriod) ||
      weekToken ||
      reportingWeek ||
      weekData?.currentWeek;
    return week ? toCanonicalWeekToken(week) : undefined;
  }, [tab, selectedCell, weekToken, reportingWeek, weekData]);

  const {
    data: summaries,
    isFetching,
    refetch,
  } = useGetWeekCalloutsQuery(
    { dashboardId, reportingPeriod: period ?? '', dataMode: apiDataMode },
    { skip: !open || !period },
  );
  const rows = useMemo(() => summaries ?? [], [summaries]);

  const weekOptions: Option[] = useMemo(() => {
    const weeks = weekData?.weeks ?? [];
    return weeks
      .slice()
      .reverse()
      .map((w) => ({ label: toWeekLabel(w), value: toCanonicalWeekToken(w) }));
  }, [weekData]);

  const definitions = definitionsResponse?.definitions;
  const categoryOptions: Option[] = useMemo(() => {
    const groups = Array.from(
      new Set((definitions ?? []).map((d) => d.metricGroup).filter(Boolean)),
    ).sort();
    return [ALL_GROUPS, ...groups.map((g) => ({ label: g, value: g }))];
  }, [definitions]);
  const metricOptions: Option[] = useMemo(() => {
    const opts = (definitions ?? [])
      .filter((d) => !category || d.metricGroup === category)
      .map((d) => ({ label: d.name, value: d.metricKey }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [ALL_METRICS, ...opts];
  }, [definitions, category]);

  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (category && groupOf(r.metricKey) !== category) return false;
      if (metricFilter && r.metricKey !== metricFilter) return false;
      if (
        needle &&
        !`${nameOf(r.metricKey)} ${r.calloutPreview ?? ''}`
          .toLowerCase()
          .includes(needle)
      )
        return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, category, metricFilter, search, catalog]);

  // ── Selection helpers ──
  const selectFromSummary = (s: CellCalloutSummary) =>
    dispatch(
      calloutCellSelected({
        dashboardId,
        metricKey: s.metricKey,
        reportingPeriod: s.reportingPeriod,
        dataMode: apiDataMode,
        // Empty until the catalog resolves so the panel's own fallback can
        // pick up the name later, instead of pinning the raw key.
        metricName: catalog.has(s.metricKey) ? nameOf(s.metricKey) : '',
        group: groupOf(s.metricKey),
        metricCategory: s.metricCategory,
        weekLabel: period ? toWeekLabel(period) : s.reportingPeriod,
      }),
    );

  // A cell's identity is (metricCategory, metricKey), not metricKey alone: once
  // records carry a network, one week can hold several summaries per metricKey
  // under different categories. Keying by metricKey alone would emit duplicate
  // React keys and make the pager resolve to the wrong cell.
  const cellRowId = (r: { metricKey: string; metricCategory?: string }) =>
    `${r.metricCategory ?? ''}#${r.metricKey}`;

  // Pager over the week's callout cells (Figma "‹ 1/12 callouts ›"). Walks
  // visibleRows — the SAME filtered set the All-tab list and CSV export show —
  // so the "n/m" count matches what the user is browsing and the pager never
  // jumps to a cell hidden by the Category/Metric/Search filters.
  const pagerIndex = selectedCell
    ? visibleRows.findIndex((r) => cellRowId(r) === cellRowId(selectedCell))
    : -1;
  const gotoPager = (delta: number) => {
    const len = visibleRows.length;
    if (len === 0) return;
    // When the current cell isn't in visibleRows (filtered out), pagerIndex is
    // -1. Step in from just OUTSIDE the ends so forward lands on the first item
    // and backward on the last — otherwise -1→0 would skip index 0 on the way
    // forward and never reach it on the way back.
    const from = pagerIndex < 0 ? (delta > 0 ? -1 : len) : pagerIndex;
    const next = (((from + delta) % len) + len) % len;
    selectFromSummary(visibleRows[next]);
  };

  // ── Download the week's callouts as CSV ──
  const downloadCsv = () => {
    const header = [
      'Metric',
      'Group',
      'Callout',
      'Comments',
      'Last author',
      'Last updated',
    ];
    const lines = [header.join(',')].concat(
      visibleRows.map((r) =>
        [
          nameOf(r.metricKey),
          groupOf(r.metricKey),
          r.calloutPreview ?? '',
          String(r.commentCount),
          r.lastAuthor,
          r.lastUpdatedAt,
        ]
          .map((f) => csvField(String(f)))
          .join(','),
      ),
    );
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wbr-callouts-${(period ?? 'week').replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Metric-tab thread data + write handlers ──
  const anchor = selectedCell ? selectionAnchor(selectedCell) : null;
  const { data: cell, isLoading: cellLoading } = useGetCellCommentsQuery(
    anchor!,
    {
      // Skip when the panel is collapsed too (not just when no cell is selected):
      // the `if (!open)` early return runs AFTER the hooks, so without `!open`
      // here a selected-then-collapsed cell keeps an active subscription and
      // refetches the thread while nothing is on screen (as getWeekCallouts does).
      skip: !open || !anchor,
    },
  );
  const [writeCallout, writeCalloutState] = useWriteCalloutMutation();
  const [deleteCallout] = useDeleteCalloutMutation();
  const [createComment, createCommentState] = useCreateCommentMutation();
  const [editComment, editCommentState] = useEditCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();
  const [createReply, createReplyState] = useCreateReplyMutation();
  const [editReply, editReplyState] = useEditReplyMutation();
  const [deleteReply] = useDeleteReplyMutation();

  const callout = cell?.callout ?? null;
  const comments = cell?.comments ?? [];

  // Author-only edit/delete on comments & replies, mirroring the server's own
  // author check. Two ways a record can be ours, because our alias is not known
  // up front — the deployed page is served anonymously, so there is no injected
  // login to read (see accessors/currentUser):
  //
  //   1. We created it in this browser and remembered its id — true immediately.
  //   2. Its author matches the alias learned from a record of ours the server
  //      echoed back — true for records written in earlier sessions too.
  //
  // Comparing the raw author against currentUserAlias() alone left the alias at
  // its local-dev fallback on every deployed stage, so nobody could edit or
  // delete their own comment. The callout headline stays editable by anyone
  // (CTS parity — it's the one shared callout).
  const isMine = (id: string, author: string): boolean =>
    isOwnRecord(id) || sameActor(author, currentUserAlias());

  // Run a write, clearing then surfacing a friendly error on failure — the
  // mutation's .unwrap() rejects on a ReportingApiError (CTS parity: it showed
  // ERROR_* strings inline instead of failing silently).
  const runWrite = async (
    op: () => Promise<unknown>,
    errorMsg: string,
    onOk: () => void,
  ) => {
    setWriteError(null);
    try {
      await op();
      onOk();
    } catch {
      setWriteError(errorMsg);
    }
  };

  const submitCallout = () => {
    if (!anchor || !calloutDraft.trim()) return;
    void runWrite(
      () =>
        writeCallout({
          anchor,
          body: calloutDraft.trim(),
          existingId: callout?.id ?? null,
        }).unwrap(),
      ERROR_POST_CALLOUT,
      () => {
        setEditingCallout(false);
        setCalloutDraft('');
      },
    );
  };
  const submitNewComment = () => {
    if (!anchor || !newComment.trim()) return;
    void runWrite(
      () => createComment({ anchor, body: newComment.trim() }).unwrap(),
      ERROR_ADD_COMMENT,
      () => setNewComment(''),
    );
  };
  const submitEditComment = (id: string) => {
    if (!anchor || !commentDraft.trim()) return;
    void runWrite(
      () => editComment({ anchor, id, body: commentDraft.trim() }).unwrap(),
      ERROR_EDIT_COMMENT,
      () => {
        setEditingCommentId(null);
        setCommentDraft('');
      },
    );
  };
  const submitReply = (commentId: string) => {
    if (!anchor || !replyDraft.trim()) return;
    void runWrite(
      () =>
        createReply({ anchor, commentId, body: replyDraft.trim() }).unwrap(),
      ERROR_ADD_REPLY,
      () => {
        setReplyingToId(null);
        setReplyDraft('');
      },
    );
  };
  const submitEditReply = (id: string) => {
    if (!anchor || !replyEditDraft.trim()) return;
    void runWrite(
      () => editReply({ anchor, id, body: replyEditDraft.trim() }).unwrap(),
      ERROR_EDIT_REPLY,
      () => {
        setEditingReplyId(null);
        setReplyEditDraft('');
      },
    );
  };

  // Destructive actions open a Cloudscape confirmation modal (not a native
  // window.confirm), then run the delete and surface a friendly error.
  // Clear any stale writeError from a prior failed write when opening the
  // confirmation modal — otherwise the modal (which now renders writeError in
  // its body) would show a leftover error before the user has done anything.
  const removeCallout = () => {
    if (anchor && callout) {
      setWriteError(null);
      setPendingDelete({ kind: 'callout', id: callout.id });
    }
  };
  const removeComment = (id: string) => {
    if (anchor) {
      setWriteError(null);
      setPendingDelete({ kind: 'comment', id });
    }
  };
  const removeReply = (id: string) => {
    if (anchor) {
      setWriteError(null);
      setPendingDelete({ kind: 'reply', id });
    }
  };

  const confirmPendingDelete = async () => {
    if (!anchor || !pendingDelete) return;
    const { kind, id } = pendingDelete;
    const [op, errorMsg]: [() => Promise<unknown>, string] =
      kind === 'callout'
        ? [() => deleteCallout({ anchor, id }).unwrap(), ERROR_DELETE_CALLOUT]
        : kind === 'comment'
        ? [() => deleteComment({ anchor, id }).unwrap(), ERROR_DELETE_COMMENT]
        : [() => deleteReply({ anchor, id }).unwrap(), ERROR_DELETE_REPLY];
    setDeleting(true);
    // Close the modal ONLY on success (the onOk callback fires only when op()
    // resolves). A failed delete keeps the modal open with the inline error so
    // the user can retry, instead of closing as if it worked.
    await runWrite(op, errorMsg, () => setPendingDelete(null));
    setDeleting(false);
  };

  // Glossary: a static reference with no metric cells — render nothing (not
  // even the collapsed rail). Placed after all hooks so hook order is stable.
  if (hiddenForTab) return null;

  if (!open) {
    return (
      <button
        type="button"
        className="callout-rail__tab"
        onClick={() => setOpen(true)}
        aria-label="Open WBR callouts"
      >
        <span aria-hidden="true" className="callout-rail__tab-icon">
          <Icon name="contact" variant="inverted" />
        </span>
        {/* Rotated book-spine label (title case, matches the panel title). */}
        <span className="callout-rail__tab-label">WBR Callouts</span>
      </button>
    );
  }

  const selectedWeekOption =
    weekOptions.find((o) => o.value === period) ??
    (period ? { label: toWeekLabel(period), value: period } : null);

  return (
    <div
      className="callout-rail__panel"
      role="complementary"
      aria-label="WBR callouts"
    >
      <div className="callout-rail__header">
        <Header
          variant="h3"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="icon"
                iconName="download"
                ariaLabel="Download callouts (CSV)"
                disabled={visibleRows.length === 0}
                onClick={downloadCsv}
              />
              <Button
                variant="icon"
                iconName="refresh"
                ariaLabel="Refresh callouts"
                disabled={!period}
                onClick={() => refetch()}
              />
              <Button
                variant="icon"
                iconName="close"
                ariaLabel="Collapse callouts"
                onClick={() => setOpen(false)}
              />
            </SpaceBetween>
          }
        >
          WBR Callouts
        </Header>
      </div>

      <div className="callout-rail__body">
        <SpaceBetween direction="vertical" size="s">
          {/* Week selector. On the Metric tab `period` is locked to the
              selected cell's week (the thread query is anchored to it), so the
              control can't change the week there — disable it rather than let
              it look interactive but snap back. It drives the week on the All tab. */}
          <FormField label="Week">
            <Select
              selectedOption={selectedWeekOption}
              options={weekOptions}
              disabled={tab === 'metric' && !!selectedCell}
              onChange={({ detail }) =>
                setWeekToken(detail.selectedOption?.value ?? '')
              }
              filteringType="auto"
            />
          </FormField>

          {/* Tabs */}
          <div className="callout-panel__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'metric'}
              className={`callout-panel__tab${
                tab === 'metric' ? ' callout-panel__tab--active' : ''
              }`}
              onClick={() => setTab('metric')}
            >
              Metric
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'all'}
              className={`callout-panel__tab${
                tab === 'all' ? ' callout-panel__tab--active' : ''
              }`}
              onClick={() => setTab('all')}
            >
              All
            </button>
          </div>

          {tab === 'metric' ? (
            !selectedCell ? (
              // Page-aware instruction: metric cells exist only on the
              // authoring page's tables (the Detailed View). Telling a Home/
              // Domain-page reader to "select a cell in the table" points at
              // something that isn't there — offer the All tab first and a
              // link over to the page that has cells.
              <Box color="text-body-secondary" padding={{ vertical: 's' }}>
                {canWrite ? (
                  <>
                    Select a metric cell in the table, or pick one from the{' '}
                    <Link fontSize="inherit" onFollow={() => setTab('all')}>All</Link> tab.
                  </>
                ) : (
                  <>
                    Pick a callout from the{' '}
                    <Link fontSize="inherit" onFollow={() => setTab('all')}>All</Link> tab
                    {authoringPath && (
                      <>
                        , or select a metric cell in the{' '}
                        <Link
                          fontSize="inherit"
                          onFollow={goToAuthoringPage}
                        >
                          {authoringPage?.title ?? 'Detailed View'}
                        </Link>{' '}
                        tables
                      </>
                    )}
                    .
                  </>
                )}
              </Box>
            ) : (
              <SpaceBetween direction="vertical" size="m">
                {/* Value-context card: current vs prior week */}
                <div className="callout-panel__context">
                  <Box variant="strong">
                    {selectedCell.metricName || nameOf(selectedCell.metricKey)}
                  </Box>
                  {/* Subtitle is the cell's NETWORK (metricCategory, the CTS
                      visual title), NOT the catalog metricGroup — a callout is
                      keyed to a network/section, so that is the context that
                      matters here. Pager/All-tab selections carry it once
                      records ship a network; until then those show nothing
                      rather than the wrong grouping. */}
                  {selectedCell.metricCategory && (
                    <Box fontSize="body-s" color="text-body-secondary">
                      {selectedCell.metricCategory.toUpperCase()}
                    </Box>
                  )}
                  {/* Values render only when the selection CARRIES them. A
                      grid cell click dispatches current/prior directly; pager
                      and All-tab selections arrive value-less (from
                      CellCalloutSummary) and the table section holding the
                      cell ENRICHES them in place (MetricTableSection's
                      enrichment effect), so this card looks the same either
                      way. Only when no rendered section holds the cell do the
                      values stay absent — and showing '—' then would read as
                      "no data this week", a different (wrong) claim. */}
                  {(selectedCell.currentValue !== undefined ||
                    selectedCell.priorValue !== undefined) && (
                    <div className="callout-panel__context-values">
                      <div>
                        <div className="callout-panel__context-week callout-panel__context-week--current">
                          {selectedCell.weekLabel} (current)
                        </div>
                        <div>{selectedCell.currentValue ?? '—'}</div>
                      </div>
                      <div>
                        <div className="callout-panel__context-week">
                          {selectedCell.priorWeekLabel
                            ? `${selectedCell.priorWeekLabel} (prior)`
                            : 'Prior'}
                        </div>
                        <div>{selectedCell.priorValue ?? '—'}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Off the authoring page the thread is read-only (R1.7). */}
                {!canWrite && (
                  <Box fontSize="body-s" color="text-body-secondary">
                    Read-only view — write callouts and comments from the{' '}
                    <Link
                      fontSize="inherit"
                      onFollow={goToAuthoringPage}
                    >
                      {authoringPage?.title ?? 'Detailed View'}
                    </Link>{' '}
                    page.
                  </Box>
                )}

                {writeError && (
                  <Box color="text-status-error" fontSize="body-s">
                    {writeError}
                  </Box>
                )}

                {cellLoading ? (
                  <Box textAlign="center" padding={{ vertical: 'l' }}>
                    <Spinner /> Loading callout…
                  </Box>
                ) : (
                  <SpaceBetween direction="vertical" size="m">
                    {/* Callout — one per cell */}
                    <div>
                      <div className="metric-callout-panel__label">
                        Callout
                      </div>
                      {editingCallout && canWrite ? (
                        <SpaceBetween direction="vertical" size="xs">
                          <Textarea
                            value={calloutDraft}
                            onChange={({ detail }) =>
                              setCalloutDraft(detail.value)
                            }
                            placeholder={PLACEHOLDER_CALLOUT}
                            rows={5}
                          />
                          <SpaceBetween direction="horizontal" size="xs">
                            <Button
                              variant="primary"
                              loading={writeCalloutState.isLoading}
                              disabled={
                                !calloutDraft.trim() ||
                                calloutDraft.trim() === (callout?.body ?? '')
                              }
                              onClick={submitCallout}
                            >
                              {BTN_POST_CALLOUT}
                            </Button>
                            <Button
                              variant="link"
                              onClick={() => setEditingCallout(false)}
                            >
                              {BTN_CANCEL}
                            </Button>
                          </SpaceBetween>
                        </SpaceBetween>
                      ) : callout ? (
                        <SpaceBetween direction="vertical" size="xxs">
                          <div>{callout.body}</div>
                          <Box fontSize="body-s" color="text-body-secondary">
                            <CalloutAuthor author={callout.lastEditedBy} /> ·{' '}
                            {relativeTime(callout.lastEditedAt)}
                          </Box>
                          {canWrite && (
                            <SpaceBetween direction="horizontal" size="xs">
                              <Button
                                variant="inline-link"
                                iconName="edit"
                                onClick={() => {
                                  setCalloutDraft(callout.body);
                                  setEditingCallout(true);
                                  setWriteError(null);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="inline-link"
                                iconName="remove"
                                onClick={removeCallout}
                              >
                                Delete
                              </Button>
                            </SpaceBetween>
                          )}
                        </SpaceBetween>
                      ) : (
                        <SpaceBetween direction="vertical" size="xxs">
                          <Box color="text-body-secondary">
                            No callout exists.
                          </Box>
                          {canWrite && (
                            <Link
                              fontSize="inherit"
                              onFollow={() => {
                                setCalloutDraft('');
                                setEditingCallout(true);
                              }}
                            >
                              Write callout
                            </Link>
                          )}
                        </SpaceBetween>
                      )}
                    </div>

                    {/* Comments + replies */}
                    <div>
                      <div className="metric-callout-panel__label">
                        Comments{comments.length ? ` (${comments.length})` : ''}
                      </div>
                      <SpaceBetween direction="vertical" size="s">
                        {comments.length === 0 && (
                          <Box color="text-body-secondary">
                            No comments yet.
                          </Box>
                        )}
                        {comments.map((comment: Comment) => (
                          <div
                            key={comment.id}
                            className="metric-callout-panel__comment"
                          >
                            {editingCommentId === comment.id && canWrite ? (
                              <SpaceBetween direction="vertical" size="xs">
                                <Textarea
                                  value={commentDraft}
                                  onChange={({ detail }) =>
                                    setCommentDraft(detail.value)
                                  }
                                  rows={3}
                                />
                                <SpaceBetween direction="horizontal" size="xs">
                                  <Button
                                    variant="primary"
                                    loading={editCommentState.isLoading}
                                    disabled={
                                      !commentDraft.trim() ||
                                      commentDraft.trim() === comment.body
                                    }
                                    onClick={() =>
                                      submitEditComment(comment.id)
                                    }
                                  >
                                    {BTN_SAVE}
                                  </Button>
                                  <Button
                                    variant="link"
                                    onClick={() => setEditingCommentId(null)}
                                  >
                                    {BTN_CANCEL}
                                  </Button>
                                </SpaceBetween>
                              </SpaceBetween>
                            ) : (
                              <SpaceBetween direction="vertical" size="xxs">
                                <div>{comment.body}</div>
                                <Box
                                  fontSize="body-s"
                                  color="text-body-secondary"
                                >
                                  <CalloutAuthor author={comment.author} /> ·{' '}
                                  {relativeTime(comment.updatedAt)}
                                </Box>
                                {canWrite && (
                                  <SpaceBetween
                                    direction="horizontal"
                                    size="xs"
                                  >
                                    {/* Reply is open to everyone; Edit/Delete only to the author.
                                      Separate conditionals (no Fragment) — SpaceBetween flattens
                                      Fragment children in React 18 and warns. */}
                                    <Button
                                      variant="inline-link"
                                      onClick={() => {
                                        setReplyingToId(comment.id);
                                        setReplyDraft('');
                                      }}
                                    >
                                      {BTN_ADD_REPLY}
                                    </Button>
                                    {isMine(comment.id, comment.author) && (
                                      <Button
                                        variant="inline-link"
                                        onClick={() => {
                                          setEditingCommentId(comment.id);
                                          setCommentDraft(comment.body);
                                          setWriteError(null);
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    )}
                                    {isMine(comment.id, comment.author) && (
                                      <Button
                                        variant="inline-link"
                                        onClick={() =>
                                          removeComment(comment.id)
                                        }
                                      >
                                        Delete
                                      </Button>
                                    )}
                                  </SpaceBetween>
                                )}
                              </SpaceBetween>
                            )}

                            {comment.replies.length > 0 && (
                              <div className="metric-callout-panel__replies">
                                <SpaceBetween direction="vertical" size="xs">
                                  {comment.replies.map((reply) => (
                                    <div key={reply.id}>
                                      {editingReplyId === reply.id &&
                                      canWrite ? (
                                        <SpaceBetween
                                          direction="vertical"
                                          size="xs"
                                        >
                                          <Textarea
                                            value={replyEditDraft}
                                            onChange={({ detail }) =>
                                              setReplyEditDraft(detail.value)
                                            }
                                            rows={2}
                                          />
                                          <SpaceBetween
                                            direction="horizontal"
                                            size="xs"
                                          >
                                            <Button
                                              variant="primary"
                                              loading={editReplyState.isLoading}
                                              disabled={
                                                !replyEditDraft.trim() ||
                                                replyEditDraft.trim() ===
                                                  reply.body
                                              }
                                              onClick={() =>
                                                submitEditReply(reply.id)
                                              }
                                            >
                                              {BTN_SAVE}
                                            </Button>
                                            <Button
                                              variant="link"
                                              onClick={() =>
                                                setEditingReplyId(null)
                                              }
                                            >
                                              {BTN_CANCEL}
                                            </Button>
                                          </SpaceBetween>
                                        </SpaceBetween>
                                      ) : (
                                        <SpaceBetween
                                          direction="vertical"
                                          size="xxs"
                                        >
                                          <div>{reply.body}</div>
                                          <Box
                                            fontSize="body-s"
                                            color="text-body-secondary"
                                          >
                                            <CalloutAuthor
                                              author={reply.author}
                                            />{' '}
                                            · {relativeTime(reply.updatedAt)}
                                          </Box>
                                          {canWrite &&
                                            isMine(reply.id, reply.author) && (
                                              <SpaceBetween
                                                direction="horizontal"
                                                size="xs"
                                              >
                                                <Button
                                                  variant="inline-link"
                                                  onClick={() => {
                                                    setEditingReplyId(reply.id);
                                                    setReplyEditDraft(
                                                      reply.body,
                                                    );
                                                    setWriteError(null);
                                                  }}
                                                >
                                                  Edit
                                                </Button>
                                                <Button
                                                  variant="inline-link"
                                                  onClick={() =>
                                                    removeReply(reply.id)
                                                  }
                                                >
                                                  Delete
                                                </Button>
                                              </SpaceBetween>
                                            )}
                                        </SpaceBetween>
                                      )}
                                    </div>
                                  ))}
                                </SpaceBetween>
                              </div>
                            )}

                            {replyingToId === comment.id && canWrite && (
                              <div className="metric-callout-panel__replies">
                                <SpaceBetween direction="vertical" size="xs">
                                  <Textarea
                                    value={replyDraft}
                                    onChange={({ detail }) =>
                                      setReplyDraft(detail.value)
                                    }
                                    placeholder={PLACEHOLDER_REPLY}
                                    rows={2}
                                  />
                                  <SpaceBetween
                                    direction="horizontal"
                                    size="xs"
                                  >
                                    <Button
                                      variant="primary"
                                      loading={createReplyState.isLoading}
                                      disabled={!replyDraft.trim()}
                                      onClick={() => submitReply(comment.id)}
                                    >
                                      {BTN_ADD_REPLY}
                                    </Button>
                                    <Button
                                      variant="link"
                                      onClick={() => setReplyingToId(null)}
                                    >
                                      {BTN_CANCEL}
                                    </Button>
                                  </SpaceBetween>
                                </SpaceBetween>
                              </div>
                            )}
                          </div>
                        ))}

                        {canWrite && (
                          <SpaceBetween direction="vertical" size="xs">
                            <Textarea
                              value={newComment}
                              onChange={({ detail }) =>
                                setNewComment(detail.value)
                              }
                              placeholder={PLACEHOLDER_COMMENT}
                              rows={3}
                            />
                            <Button
                              variant="normal"
                              loading={createCommentState.isLoading}
                              disabled={!newComment.trim()}
                              onClick={submitNewComment}
                            >
                              {BTN_ADD_COMMENT}
                            </Button>
                          </SpaceBetween>
                        )}
                      </SpaceBetween>
                    </div>

                    {/* Thread-scoped action items (feature-gated inside). */}
                    {anchor && <ThreadActionItems anchor={anchor} />}

                    {/* Pager: step through the week's callout cells */}
                    <div className="callout-panel__pager">
                      <Button
                        variant="icon"
                        iconName="angle-left"
                        ariaLabel="Previous callout"
                        // Disabled only when there's nowhere to go: no rows, or
                        // the current cell IS the sole row. When the current
                        // cell isn't in the list (pagerIndex -1), navigation must
                        // stay enabled so the user can step onto the callouts.
                        disabled={
                          visibleRows.length === 0 ||
                          (pagerIndex >= 0 && visibleRows.length <= 1)
                        }
                        onClick={() => gotoPager(-1)}
                      />
                      <Box fontSize="body-s" color="text-body-secondary">
                        {visibleRows.length > 0
                          ? // pagerIndex is -1 when the current cell carries no
                            // callout (not in the roll-up); -1 + 1 = 0 renders
                            // "0/m", not a false "1/m" implying it's the first.
                            `${pagerIndex + 1}/${visibleRows.length} callouts`
                          : '0 callouts'}
                      </Box>
                      <Button
                        variant="icon"
                        iconName="angle-right"
                        ariaLabel="Next callout"
                        disabled={
                          visibleRows.length === 0 ||
                          (pagerIndex >= 0 && visibleRows.length <= 1)
                        }
                        onClick={() => gotoPager(1)}
                      />
                    </div>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            )
          ) : (
            /* ── All tab: the week roll-up list ── */
            <SpaceBetween direction="vertical" size="s">
              <button
                type="button"
                className="callout-rail__filters-toggle"
                onClick={() => setFiltersOpen((v) => !v)}
              >
                <span>Filters</span>
                <span>
                  {filtersOpen ? 'Hide' : 'Show'}{' '}
                  <Icon
                    name={filtersOpen ? 'angle-up' : 'angle-down'}
                    size="small"
                  />
                </span>
              </button>
              {filtersOpen && (
                <SpaceBetween direction="vertical" size="xs">
                  <FormField label="Metric group">
                    <Select
                      selectedOption={
                        categoryOptions.find((o) => o.value === category) ??
                        ALL_GROUPS
                      }
                      options={categoryOptions}
                      onChange={({ detail }) => {
                        setCategory(detail.selectedOption?.value ?? '');
                        setMetricFilter('');
                      }}
                    />
                  </FormField>
                  <FormField label="Metric">
                    <Select
                      selectedOption={
                        metricOptions.find((o) => o.value === metricFilter) ??
                        ALL_METRICS
                      }
                      options={metricOptions}
                      onChange={({ detail }) =>
                        setMetricFilter(detail.selectedOption?.value ?? '')
                      }
                      filteringType="auto"
                    />
                  </FormField>
                  <FormField label="Search">
                    <Input
                      value={search}
                      onChange={({ detail }) => setSearch(detail.value)}
                      placeholder="Search callouts…"
                      type="search"
                    />
                  </FormField>
                </SpaceBetween>
              )}

              <Box fontSize="body-s" color="text-body-secondary">
                {period ? toWeekLabel(period) : 'Loading week…'}
                {' · Snapshot'}
              </Box>

              {isFetching ? (
                <Box textAlign="center" padding="s">
                  <Spinner /> Loading callouts…
                </Box>
              ) : visibleRows.length === 0 ? (
                <Box color="text-body-secondary">
                  {rows.length === 0
                    ? 'No callouts for this week.'
                    : 'No callouts match the filters.'}
                </Box>
              ) : (
                <>
                  <Box fontSize="body-s" color="text-body-secondary">
                    {visibleRows.length} cell
                    {visibleRows.length === 1 ? '' : 's'} with callouts or
                    comments
                  </Box>
                  {visibleRows.map((row) => (
                    <button
                      key={cellRowId(row)}
                      type="button"
                      className="callout-rail__item callout-rail__item--button"
                      onClick={() => selectFromSummary(row)}
                    >
                      {/* Eyebrow is the cell's NETWORK (metricCategory), not
                          the catalog metricGroup — same rule as the Metric
                          card: a callout is keyed to a network/section. */}
                      {row.metricCategory && (
                        <Box fontSize="body-s" color="text-body-secondary">
                          {row.metricCategory}
                        </Box>
                      )}
                      <Box variant="strong">{nameOf(row.metricKey)}</Box>
                      {row.calloutPreview ? (
                        <div className="callout-rail__preview">
                          {row.calloutPreview}
                        </div>
                      ) : (
                        <Box fontSize="body-s" color="text-body-secondary">
                          {row.commentCount} comment
                          {row.commentCount === 1 ? '' : 's'}, no callout
                        </Box>
                      )}
                      {/* Author-line gap tuned to visually match the
                          detailed-view thread (its body text carries more
                          line-height than the clamped 13px preview here). */}
                      <Box
                        fontSize="body-s"
                        color="text-body-secondary"
                        margin={{ top: 'xs' }}
                      >
                        <CalloutAuthor author={row.lastAuthor} />
                        {row.commentCount > 0 && (
                          <>
                            {' · '}
                            <Icon name="contact" size="inherit" />{' '}
                            {row.commentCount}
                          </>
                        )}
                      </Box>
                    </button>
                  ))}
                </>
              )}
              {selectedCell && (
                <Button
                  variant="link"
                  onClick={() => dispatch(calloutCellCleared())}
                >
                  Clear selected metric
                </Button>
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </div>

      {/* Delete confirmation — only reachable from write affordances, so it
          mounts only on the authoring page (R1.7). Cloudscape Modal keeps its
          DOM mounted while hidden; skipping the mount keeps read-only pages
          genuinely free of write UI. */}
      {canWrite && (
        <Modal
          visible={!!pendingDelete}
          onDismiss={() => setPendingDelete(null)}
          header={`Delete ${pendingDelete?.kind ?? ''}`}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setPendingDelete(null)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  loading={deleting}
                  onClick={confirmPendingDelete}
                >
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="s" direction="vertical">
            <Box>
              {pendingDelete
                ? `Delete this ${pendingDelete.kind}? This can't be undone.`
                : ''}
            </Box>
            {/* A failed delete keeps this modal open; the shared writeError also
              renders in the Metric-tab body BEHIND the modal, so surface it here
              too or the user sees the modal stay open with no explanation. */}
            {writeError && (
              <Box color="text-status-error" fontSize="body-s">
                {writeError}
              </Box>
            )}
          </SpaceBetween>
        </Modal>
      )}
    </div>
  );
};
